import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, ChevronDown, ChevronUp, FileSpreadsheet, Edit2, Check, X as XIcon, Search, Loader2 } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import ThreeStepProgress from '../ThreeStepProgress';
import UploadConfirmationModal from '../UploadConfirmationModal';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { useListPortfolioSitesQuery, useListAdminSitesQuery, useUpdatePortfolioSiteMutation, useLazyGetPortfolioSiteQuery } from '../../redux/api/portfolioApi';
import { useGetOrganizationMembersQuery } from '../../redux/api/organizationApi';
import { useDeleteDocumentMutation, useDownloadArtefactMutation, useLazyExportDocumentExcelQuery, useLazyBulkExportExcelQuery } from '../../redux/api/documentsApi';
import { setMembers } from '../../redux/slices/organizationSlice';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import { formatDateTime, toSentenceCase } from '../../utils/dateUtils';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config/apiConfig';

interface SiteListItem {
  id: string;
  name: string;
  asbuilt_name: string | null;
  created_at: string;
  uploaded_by: string | null;
  uploader?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  } | null;
  portfolio_id: string;
  tenant_id: string;
}

interface AsBuiltDocument {
  document_id: string;
  title: string;
  file_path: string;
  uploaded_at: string;
  uploaded_by: string;
  uploader?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
  portfolio_name?: string;
  site_id: string;
  site_name?: string;
  tenant_id?: string;
}

interface DemoUpload {
  id: string;
  fileName: string;
  step: 'upload' | 'processing' | 'done' | 'failed';
  siteId: string;
  showRetry?: boolean;
  failureType?: 'upload' | 'processing';
}

type SortOrder = 'asc' | 'desc';

const AsBuiltListTab: React.FC = () => {
  const { state, setSelectedSite } = useAppContext();
  const dispatch = useAppDispatch();
  const [sites, setSites] = useState<SiteListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filter and sort states
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  // selectedUser stores the member's email (or 'all')
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  // Applied date filters used for querying backend – updated when "To" is filled
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const authProfile = useAppSelector((s) => s.auth.profile);
  const organizationState = useAppSelector((s) => s.organization);

  const userEmail = (authProfile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');

  // Normalize tenant from profile – backend may return a string id or a full object
  const rawTenant = authProfile.tenant as any;
  const userTenantId: string | null = React.useMemo(() => {
    if (!rawTenant) return null;
    if (typeof rawTenant === 'string') return rawTenant;
    if (typeof rawTenant === 'object') {
      return rawTenant.id || rawTenant.tenant_id || null;
    }
    return null;
  }, [rawTenant]);

  // For super admins, use currently selected organization (set from AdminViewTab)
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Get selected portfolio from Redux (for non‑super‑admins)
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);

  // Effective tenant for organization members:
  // - Super admin: use currently selected organization
  // - Other users: use their own tenant (normalized id)
  const effectiveTenantId = isSuperAdmin ? currentOrgId : userTenantId;
  
  // Super admin: fetch sites via /api/v1/admin/sites
  // - If an organization is selected: pass organization_id to filter by that org
  // - If no organization is selected: call without organization_id to load super admin's own sites
  const adminArgs = isSuperAdmin
    ? {
        ...(currentOrgId ? { organization_id: currentOrgId } : {}),
        // Format dates to ISO 8601: YYYY-MM-DD from input -> YYYY-MM-DDTHH:mm:ss
        // Both picker and manual entry produce YYYY-MM-DD format (validated by browser)
        ...(appliedDateFrom ? { uploaded_after: `${appliedDateFrom}T00:00:00` } : {}),
        ...(appliedDateTo ? { uploaded_before: `${appliedDateTo}T23:59:59` } : {}),
      }
    : undefined;

  const {
    data: adminSitesResponse,
    isLoading: adminLoading,
    error: adminSitesError,
    refetch: refetchAdminSites,
  } = useListAdminSitesQuery(adminArgs, {
    skip: !isSuperAdmin,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Fetch organization members for the current tenant (used for "User" filter)
  const { data: membersData } = useGetOrganizationMembersQuery(
    { tenant_id: effectiveTenantId as string, _limit: 100, _offset: 0 },
    {
      skip: !effectiveTenantId,
    }
  );

  // Keep members in Redux slice so other views can reuse
  useEffect(() => {
    if (membersData) {
      dispatch(setMembers(membersData));
    }
  }, [membersData, dispatch]);

  // Build user options for filters from organization members
  const organizationMembers = useAppSelector((s) => s.organization.members);

  const getMemberDisplayName = (member: any): string => {
    const user = member?.user;
    if (user) {
      const first = (user.first_name || '').trim();
      const last = (user.last_name || '').trim();
      const full = [first, last].filter(Boolean).join(' ');
      if (full) return full;
      if (user.email) return user.email;
    }
    return member.user_id || '';
  };

  const getMemberEmail = (member: any): string => {
    const user = member?.user;
    if (user?.email) return user.email;
    if (typeof member.user_id === 'string') return member.user_id;
    return '';
  };

  // Resolve uploader_name (first + last name) for the currently selected user (email)
  const selectedUploaderName = React.useMemo(() => {
    if (selectedUser === 'all') return undefined;
    const member = organizationMembers.find((m: any) => getMemberEmail(m) === selectedUser);
    if (!member) return undefined;
    const name = getMemberDisplayName(member);
    return name || undefined;
  }, [selectedUser, organizationMembers]);

  // Other users: fetch sites by portfolio
  const {
    data: portfolioSitesResponse,
    isLoading: portfolioLoading,
    error: sitesError,
    refetch: refetchPortfolioSites,
  } = useListPortfolioSitesQuery(
    selectedPortfolioId
      ? {
          portfolio_id: selectedPortfolioId,
          uploader_name: selectedUploaderName,
          // Format dates to ISO 8601: YYYY-MM-DD from input -> YYYY-MM-DDTHH:mm:ss
          // Both picker and manual entry produce YYYY-MM-DD format (validated by browser)
          uploaded_after: appliedDateFrom ? `${appliedDateFrom}T00:00:00` : undefined,
          uploaded_before: appliedDateTo ? `${appliedDateTo}T23:59:59` : undefined,
        }
      : // Value won't be used when skipped
        { portfolio_id: '' },
    { 
      skip: isSuperAdmin || !selectedPortfolioId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const loading = isSuperAdmin ? adminLoading : portfolioLoading;

  // Mutations and queries for documents
  const [deleteDocument] = useDeleteDocumentMutation();
  const [downloadArtefact] = useDownloadArtefactMutation();
  const [triggerExportExcel] = useLazyExportDocumentExcelQuery();
  const [triggerBulkExportExcel] = useLazyBulkExportExcelQuery();

  // Mutation for updating portfolio site
  const [updatePortfolioSite, { isLoading: isUpdatingSite }] = useUpdatePortfolioSiteMutation();

  // Expanded sites (track which sites are expanded) - initialize with all sites expanded
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

  // Edit site name state
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editedSiteName, setEditedSiteName] = useState<string>('');
  const [savingSiteName, setSavingSiteName] = useState(false);

  // Demo progress state - support multiple concurrent uploads
  const [demoUploads, setDemoUploads] = useState<DemoUpload[]>([]);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'demo' | 'failure' | 'failure-upload' | 'upload' | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Download loading state
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  // Documents state (for compatibility with existing code)
  const [documents, setDocuments] = useState<AsBuiltDocument[]>([]);
  
  // Always refetch on mount to ensure fresh data (no cache)
  useEffect(() => {
    if (isSuperAdmin) {
      refetchAdminSites();
    } else if (selectedPortfolioId) {
      refetchPortfolioSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - we want to refetch once when component mounts

  // Transform and set sites data when sites are loaded
  useEffect(() => {
    const raw = isSuperAdmin ? adminSitesResponse : portfolioSitesResponse;

    if (!raw) {
      setSites([]);
      setExpandedSites(new Set());
      return;
    }

    const sitesArray = Array.isArray(raw)
      ? raw
      : (raw as any)?.items || [];
    
    const transformedSites: SiteListItem[] = sitesArray.map((site: any) => ({
      id: site.id || site.site_id,
      name: site.name || site.site_name || 'Unknown Site',
      asbuilt_name: site.asbuilt_name || null,
      created_at: site.created_at,
      uploaded_by: site.uploaded_by || null,
      uploader: site.uploader || null,
      portfolio_id: site.portfolio_id,
      tenant_id: site.tenant_id,
    }));
    
    setSites(transformedSites);
    
    if (transformedSites.length > 0) {
      const allSiteIds = new Set(transformedSites.map(site => site.id));
      setExpandedSites(allSiteIds);
    } else {
      setExpandedSites(new Set());
    }
  }, [adminSitesResponse, portfolioSitesResponse, isSuperAdmin]);

  // Set error from API – but don't treat 400/404 as hard errors (those can mean "no sites")
  useEffect(() => {
    if (!sitesError) {
      setError(null);
      return;
    }
    const err = sitesError as any;
    const status = err?.status;
    if (status === 400 || status === 404) {
      // Treat as "no sites" for this portfolio, not a failure
      setError(null);
      return;
    }
    setError('Failed to load sites');
  }, [sitesError]);

  // Build user options for filters from organization members
  const userOptions = React.useMemo(
    () => {
      const map = new Map<string, string>();
      organizationMembers.forEach((member: any) => {
        const email = getMemberEmail(member);
        if (!email) return;
        if (!map.has(email)) {
          map.set(email, getMemberDisplayName(member));
        }
      });

      const options = Array.from(map.entries()).map(([email, label]) => ({
        value: email,
        label,
      }));

      // Sort alphabetically by label
      options.sort((a, b) => a.label.localeCompare(b.label));

      return [
        { value: 'all', label: 'All Users' },
        ...options,
      ];
    },
    [organizationMembers]
  );

  // Filter sites (client-side backup; backend already filters by uploader_name and date)
  const filteredSites = sites
    .filter(site =>
      selectedUser === 'all' ||
      site.uploaded_by === selectedUploaderName ||
      site.uploader === selectedUploaderName
    )
    .filter(site => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          site.name.toLowerCase().includes(query) ||
          site.asbuilt_name?.toLowerCase().includes(query) ||
          site.uploaded_by?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .filter(site => {
      // If no date filters, show all
      if (!dateFrom && !dateTo) return true;

      const siteDate = new Date(site.created_at);

      // Only "Date From" filled: show from that date to current
      if (dateFrom && !dateTo) {
        return siteDate >= new Date(dateFrom);
      }

      // Only "Date To" filled: show from beginning of time to that date
      if (!dateFrom && dateTo) {
        return siteDate <= new Date(dateTo + 'T23:59:59');
      }

      // Both filled: show range inclusive
      if (dateFrom && dateTo) {
        return siteDate >= new Date(dateFrom) && siteDate <= new Date(dateTo + 'T23:59:59');
      }

      return true;
    });

  // Group sites by site_id for the original card structure
  const siteGroups = filteredSites.reduce((acc, site) => {
    const siteKey = site.id;
    const siteName = site.name;

    if (!acc[siteKey]) {
      acc[siteKey] = {
        site_id: siteKey,
        site_name: siteName,
        documents: [] as AsBuiltDocument[],
        // Add site metadata
        asbuilt_name: site.asbuilt_name,
        created_at: site.created_at,
        uploaded_by: site.uploaded_by,
      };
    }
    // Convert site to document format for display
    // Each site becomes a "document" entry showing its details
    acc[siteKey].documents.push({
      document_id: site.id,
      title: site.asbuilt_name || site.name, // Show asbuilt_name as title, fallback to name
      file_path: '',
      uploaded_at: site.created_at,
      uploaded_by: site.uploaded_by || 'Not available',
      uploader: site.uploader || undefined,
      site_id: site.id,
      site_name: site.name,
      tenant_id: site.tenant_id,
    });
    return acc;
  }, {} as Record<string, { site_id: string; site_name: string; documents: AsBuiltDocument[]; asbuilt_name?: string | null; created_at?: string; uploaded_by?: string | null }>);

  // Add demo upload sites (Unknown Sites) to the groups
  demoUploads.forEach(upload => {
    if (!siteGroups[upload.siteId]) {
      siteGroups[upload.siteId] = {
        site_id: upload.siteId,
        site_name: 'Unknown Site',
        documents: [],
        asbuilt_name: null,
        created_at: new Date().toISOString(),
        uploaded_by: null,
      };
    }
  });

  // Sort sites and their documents
  const sortedSites = Object.values(siteGroups).sort((a, b) => {
    // Get demo uploads for each site
    const aHasDemo = demoUploads.some(upload => upload.siteId === a.site_id);
    const bHasDemo = demoUploads.some(upload => upload.siteId === b.site_id);

    // Sites with active demo uploads always go to the top, newest first
    if (aHasDemo && bHasDemo) {
      // Compare by site_id (which includes timestamp) - newer comes first
      return b.site_id.localeCompare(a.site_id);
    }
    if (aHasDemo && !bHasDemo) return -1;
    if (!aHasDemo && bHasDemo) return 1;

    // Otherwise sort by name
    const comparison = a.site_name.localeCompare(b.site_name);
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  // Sort documents within each site
  sortedSites.forEach(site => {
    site.documents.sort((a, b) => {
      const comparison = a.title.localeCompare(b.title);
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  });

  // Toggle site expansion
  const toggleSite = (siteId: string) => {
    const newExpanded = new Set(expandedSites);
    if (newExpanded.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
    }
    setExpandedSites(newExpanded);
  };

  // Start editing site name
  const startEditingSite = (siteId: string, currentName: string) => {
    setEditingSiteId(siteId);
    setEditedSiteName(currentName);
  };
  
  // Get lazy query for fetching full site details
  const [triggerGetPortfolioSite] = useLazyGetPortfolioSiteQuery();

  // Handle site selection - update both AppContext and Redux
  const handleSiteSelect = async (siteId: string) => {
    setSelectedSite(siteId);
    
    // Find the site in the original sites array to get portfolio_id
    const site = sites.find(s => s.id === siteId);
    if (!site) {
      console.warn('Site not found in current list:', siteId);
      return;
    }

    const portfolioId = site.portfolio_id;
    if (!portfolioId) {
      console.warn('No portfolio ID found for site:', siteId);
      return;
    }

    try {
      // Fetch full site details and update Redux
      const fullSite = await triggerGetPortfolioSite({
        portfolio_id: portfolioId,
        site_id: siteId,
      }).unwrap();
      
      dispatch(setCurrentSite(fullSite));
    } catch (err) {
      console.error('Failed to fetch site details for Redux:', err);
    }
  };

  // Cancel editing
  const cancelEditingSite = () => {
    setEditingSiteId(null);
    setEditedSiteName('');
  };

  // Save edited site name
  const saveEditedSiteName = async (siteId: string) => {
    if (!editedSiteName.trim()) {
      setError('Site name cannot be empty');
      return;
    }

    // For super admins, get portfolio_id from the site object
    // For regular admins, use selectedPortfolioId from Redux
    let portfolioIdToUse: string | null = null;
    
    if (isSuperAdmin) {
      const site = sites.find(s => s.id === siteId);
      if (!site || !site.portfolio_id) {
        setError('Site not found or missing portfolio ID');
        return;
      }
      portfolioIdToUse = site.portfolio_id;
    } else {
      if (!selectedPortfolioId) {
        setError('No portfolio selected');
        return;
      }
      portfolioIdToUse = selectedPortfolioId;
    }

    setSavingSiteName(true);
    try {
      const trimmedName = editedSiteName.trim();
      
      await updatePortfolioSite({
        portfolio_id: portfolioIdToUse,
        site_id: siteId,
        body: {
          name: trimmedName,
        },
      }).unwrap();

      // Update sites with new site name
      setSites(prevSites =>
        prevSites.map(site =>
          site.id === siteId
            ? { ...site, name: trimmedName }
            : site
        )
      );

      setEditingSiteId(null);
      setEditedSiteName('');
      toast.success('Site name updated successfully');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to update site name';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSavingSiteName(false);
    }
  };

  // Delete document
  const handleDelete = async (documentId: string) => {
    setDeleting(true);
    try {
      await deleteDocument(documentId).unwrap();
      // Remove from local state
      setDocuments(docs => docs.filter(doc => doc.document_id !== documentId));
      setDeleteConfirm(null);
      toast.success('Document deleted successfully');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to delete document';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Download document
  const handleDownload = async (doc: AsBuiltDocument, siteId?: string) => {
    // Use provided siteId or fallback to doc.site_id, ensuring we use the site's id from API
    const artefactId = siteId || doc.site_id || doc.document_id;
    setDownloadingDocId(doc.document_id);
    try {
      if (!doc.tenant_id) {
        throw new Error('Tenant ID is required for download');
      }

      if (!artefactId) {
        throw new Error('Site ID is required for download');
      }

      const data = await downloadArtefact({
        artefact_id: artefactId, // Site's id from API response
        document_id: artefactId, // Also use site's id for document_id
        tenant_id: doc.tenant_id,
        filename: doc.title || 'as-built.pdf',
        expires_in: 1800, // 30 minutes expiration (max allowed)
      }).unwrap();

      const downloadUrl = data.url;
      const filename = data.filename || doc.title || 'as-built.pdf';

      if (!downloadUrl) {
        throw new Error('No download URL received from server');
      }

      // Open in new tab and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (err: any) {
      // Handle error message extraction - API might return error in different formats
      let errorMessage = 'Failed to download document';
      
      if (err?.data) {
        // Handle array of errors (validation errors)
        if (Array.isArray(err.data.detail)) {
          errorMessage = err.data.detail.map((e: any) => e.msg || e.message || String(e)).join(', ');
        } 
        // Handle single error object
        else if (err.data.detail?.msg) {
          errorMessage = err.data.detail.msg;
        }
        // Handle string error
        else if (typeof err.data.detail === 'string') {
          errorMessage = err.data.detail;
        }
        // Handle error object with message
        else if (err.data.message) {
          errorMessage = err.data.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Export to Excel
  const handleExportExcel = async (doc: AsBuiltDocument) => {
    try {
      const blob = await triggerExportExcel(doc.document_id).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.title.replace('.pdf', '')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel export downloaded');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to export to Excel';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Bulk export all documents to Excel
  const handleBulkExportExcel = async () => {
    try {
      const blob = await triggerBulkExportExcel().unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `as-builts-bulk-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Bulk Excel export downloaded');
    } catch (err: any) {
      const errorMessage = err?.data?.detail || err?.message || 'Failed to bulk export to Excel';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Demo progress flow - create a new upload instance and unknown site
  // Execute demo progress (after confirmation)
  const executeDemoProgress = () => {
    const uploadId = `demo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileName = `demo-as-built-${demoUploads.length + 1}.pdf`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: fileName,
      step: 'upload',
      siteId: siteId
    };

    // Add new upload to the list
    setDemoUploads(prev => [...prev, newUpload]);

    // Auto-expand the unknown site
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      newSet.add(siteId);
      return newSet;
    });

    // Simulate upload phase (2 seconds)
    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      // Simulate processing phase (2 seconds)
      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'done' } : upload
          )
        );

        // After 5 more seconds (total 9 seconds), remove progress bar and add dummy document
        setTimeout(() => {
          // Remove the progress bar
          setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));

          // Add dummy document to the site
          const dummyDoc: AsBuiltDocument = {
            document_id: `demo-doc-${Date.now()}`,
            title: fileName.replace('.pdf', ''),
            file_path: `/demo/${fileName}`,
            uploaded_at: new Date().toISOString(),
            uploaded_by: 'Demo User',
            site_id: siteId,
            site_name: 'Unknown Site'
          };

          setDocuments(prev => [dummyDoc, ...prev]);
        }, 5000);
      }, 2000);
    }, 2000);
  };

  // Demo progress button handler - show confirmation modal
  const handleDemoProgress = () => {
    setPendingAction('demo');
    setShowConfirmModal(true);
  };

  // Demo failure button handler - show confirmation modal
  const handleDemoFailure = () => {
    setPendingAction('failure');
    setShowConfirmModal(true);
  };

  // Demo failure upload button handler - show confirmation modal
  const handleDemoFailureUpload = () => {
    setPendingAction('failure-upload');
    setShowConfirmModal(true);
  };

  // Execute demo failure (after confirmation)
  const executeDemoFailure = () => {
    const uploadId = `demo-fail-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: `demo-as-built-${demoUploads.length + 1}.pdf`,
      step: 'upload',
      siteId: siteId
    };

    // Add new upload to the list
    setDemoUploads(prev => [...prev, newUpload]);

    // Auto-expand the unknown site
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      newSet.add(siteId);
      return newSet;
    });

    // Simulate upload phase (2 seconds)
    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      // Simulate partial processing then failure (1 second into processing)
      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'processing' } : upload
          )
        );
      }, 1000);
    }, 2000);
  };

  // Execute demo failure at upload stage (after confirmation)
  const executeDemoFailureUpload = () => {
    const uploadId = `demo-fail-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newUpload: DemoUpload = {
      id: uploadId,
      fileName: `demo-as-built-${demoUploads.length + 1}.pdf`,
      step: 'upload',
      siteId: siteId,
      showRetry: false
    };

    // Add new upload to the list
    setDemoUploads(prev => [...prev, newUpload]);

    // Auto-expand the unknown site
    setExpandedSites(prev => {
      const newSet = new Set(prev);
      newSet.add(siteId);
      return newSet;
    });

    // Simulate upload phase failure (1 second)
    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'failed', failureType: 'upload' } : upload
        )
      );
    }, 1000);
  };

  // Close specific demo progress
  const handleCloseDemoProgress = (uploadId: string) => {
    setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
  };

  // Handle confirmation modal confirm
  const handleConfirmUpload = () => {
    setShowConfirmModal(false);
    if (pendingAction === 'demo') {
      executeDemoProgress();
    } else if (pendingAction === 'failure') {
      executeDemoFailure();
    } else if (pendingAction === 'failure-upload') {
      executeDemoFailureUpload();
    } else if (pendingAction === 'upload' && pendingFile) {
      executeUpload(pendingFile);
    }
    setPendingAction(null);
    setPendingFile(null);
  };

  // Handle confirmation modal cancel
  const handleCancelUpload = () => {
    setShowConfirmModal(false);
    setPendingAction(null);
    setPendingFile(null);
  };

  // Retry failed upload
  const handleRetryDemoUpload = (uploadId: string) => {
    // Reset the upload to 'upload' step to restart the flow
    setDemoUploads(prev =>
      prev.map(upload =>
        upload.id === uploadId ? { ...upload, step: 'upload' } : upload
      )
    );

    // Simulate upload phase (2 seconds)
    setTimeout(() => {
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'processing' } : upload
        )
      );

      // Simulate partial processing then failure again (1 second into processing)
      setTimeout(() => {
        setDemoUploads(prev =>
          prev.map(upload =>
            upload.id === uploadId ? { ...upload, step: 'failed' } : upload
          )
        );
      }, 1000);
    }, 2000);
  };

  // Upload handler with 3-step progress tracking
  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file for the as-built document.');
        return;
      }

      // Store the file and show confirmation modal
      setPendingFile(file);
      setPendingAction('upload');
      setShowConfirmModal(true);
    };
    input.click();
  };

  // Execute actual upload (after confirmation)
  const executeUpload = async (file: File) => {
      const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Create a new "Unknown Site" for each upload
    const siteId = `unknown-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      try {
        setError(null);

      // Create new upload entry with 3-step progress
      const newUpload: DemoUpload = {
          id: uploadId,
          fileName: file.name,
        step: 'upload',
        siteId: siteId
      };
      setDemoUploads(prev => [...prev, newUpload]);

      // Ensure the site is expanded
      setExpandedSites(prev => {
        const newSet = new Set(prev);
        newSet.add(siteId);
        return newSet;
      });

        const formData = new FormData();
        formData.append('file', file);
      formData.append('site_id', siteId);
        formData.append('document_type_id', '3'); // As-built type
        formData.append('title', `As-Built - ${file.name.replace('.pdf', '')}`);
      formData.append('description', 'As-built document uploaded via Sites List');
        formData.append('processor_type', 'as-built');


      // Create XMLHttpRequest to track upload progress
      const xhr = new XMLHttpRequest();

      // Handle completion
      await new Promise<void>((resolve, reject) => {
          xhr.addEventListener('load', async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              // Move to processing step
              setDemoUploads(prev =>
          prev.map(upload =>
                  upload.id === uploadId ? { ...upload, step: 'processing' } : upload
                )
              );

              // Parse response to get document_id
              let documentId: string | null = null;
              try {
                const response = JSON.parse(xhr.responseText);
                documentId = response.document_id;
              } catch (e) {
                console.warn('Could not parse upload response:', e);
              }

              // Poll for processing status
              const pollProcessingStatus = async () => {
                if (!documentId) {
                  // If we don't have document_id, simulate processing (2 seconds)
        setTimeout(() => {
                    setDemoUploads(prev =>
                      prev.map(upload =>
                        upload.id === uploadId ? { ...upload, step: 'done' } : upload
                      )
                    );

                    setTimeout(() => {
                      setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
                      // Sites will be refetched automatically via the query
                    }, 5000);
        }, 2000);
                  return;
                }

                // Poll the document status
                const checkStatus = async () => {
                  try {
                    const response = await fetch(`${API_BASE_URL}/api/v1/documents/${documentId}`, {
                      headers: {
                        'Authorization': 'Bearer demo-token',
                        'Content-Type': 'application/json',
                      }
                    });

                    if (response.ok) {
                      const doc = await response.json();

                      // Check if processing is complete
                      if (doc.processing_status === 'completed' || doc.processing_status === 'manual_upload') {
                        // Move to done step
                        setDemoUploads(prev =>
          prev.map(upload =>
                            upload.id === uploadId ? { ...upload, step: 'done' } : upload
          )
        );

                        // After 5 seconds in done state, remove progress bar and refresh
        setTimeout(() => {
                          setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
                          // Sites will be refetched automatically via the query
        }, 5000);
                      } else if (doc.processing_status === 'failed') {
                        // Processing failed
                        setDemoUploads(prev =>
                          prev.map(upload =>
                            upload.id === uploadId ? { ...upload, step: 'failed' } : upload
                          )
                        );
                      } else {
                        // Still processing, check again in 1 second
                        setTimeout(checkStatus, 1000);
                      }
                    } else {
                      // Default to 2 second simulation if we can't check status
                      setTimeout(() => {
                        setDemoUploads(prev =>
                          prev.map(upload =>
                            upload.id === uploadId ? { ...upload, step: 'done' } : upload
                          )
                        );

                        setTimeout(() => {
                          setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
                          // Sites will be refetched automatically via the query
        }, 5000);
                      }, 2000);
                    }
                  } catch (error) {
                    console.error('Error checking processing status:', error);
                    // Default to 2 second simulation on error
                    setTimeout(() => {
                      setDemoUploads(prev =>
                        prev.map(upload =>
                          upload.id === uploadId ? { ...upload, step: 'done' } : upload
                        )
                      );

                        setTimeout(() => {
                          setDemoUploads(prev => prev.filter(upload => upload.id !== uploadId));
                          // Sites will be refetched automatically via the query
        }, 5000);
                    }, 2000);
                  }
                };

                checkStatus();
              };

              pollProcessingStatus();
              resolve();
            } else {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          });

          xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'));
          });

          xhr.addEventListener('abort', () => {
            reject(new Error('Upload cancelled'));
          });

          xhr.open('POST', `${API_BASE_URL}/api/v1/documents/upload`);
          xhr.setRequestHeader('Authorization', 'Bearer demo-token');
          xhr.send(formData);
        });

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
        setError(errorMessage);

      // Update upload status to failed
      setDemoUploads(prev =>
        prev.map(upload =>
          upload.id === uploadId ? { ...upload, step: 'failed' } : upload
        )
      );
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // While loading sites, show a centered loader
  if (loading || (!isSuperAdmin && !selectedPortfolioId)) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium">
            Loading sites...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col bg-engramiq-dark-blue">
      {/* Header */}
      <div className="p-6 border-b border-engramiq-green/20">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-engramiq-light-grey">Sites List</h1>
          <div className="flex gap-3">
            {/* <button
              onClick={handleDemoProgress}
              disabled={loading}
              className="px-4 py-2 bg-transparent hover:bg-engramiq-green/10 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-sm text-engramiq-green hover:text-engramiq-green transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              Demo 3-Step Progress
            </button>
            <button
              onClick={handleDemoFailure}
              disabled={loading}
              className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/30 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              Demo Failure: Processing
            </button>
            <button
              onClick={handleDemoFailureUpload}
              disabled={loading}
              className="px-4 py-2 bg-transparent hover:bg-accent-red/10 border border-accent-red/30 hover:border-accent-red rounded-lg text-sm text-accent-red hover:text-accent-red transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              Demo Failure: Upload
            </button>
            <button
              onClick={handleBulkExportExcel}
              disabled={loading || documents.length === 0}
              className="px-4 py-2 bg-transparent hover:bg-engramiq-green/10 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-sm text-engramiq-green hover:text-engramiq-green transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Bulk Export
            </button>
            <button
              onClick={handleUpload}
              disabled={loading}
              className="px-4 py-2 bg-gradient-green-blue hover:brightness-110 rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              Upload As-Builts
            </button> */}
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="flex gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-engramiq-bright-grey">Sort:</label>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey hover:border-engramiq-green transition-all flex items-center gap-2"
            >
              {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
              {sortOrder === 'asc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-engramiq-bright-grey">Date Uploaded From:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                const value = e.target.value;
                setDateFrom(value);
              }}
              className="w-full px-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-engramiq-bright-grey">Date Uploaded To:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                const value = e.target.value;
                setDateTo(value);
              }}
              onBlur={(e) => {
                // Trigger query on blur if "To" date is set
                const value = e.target.value;
                if (value) {
                  // Format: YYYY-MM-DD from input -> ISO format with time (YYYY-MM-DDTHH:mm:ss)
                  setAppliedDateFrom(dateFrom || '');
                  setAppliedDateTo(value);
                } else {
                  // Clearing "To" clears applied range
                  setAppliedDateFrom('');
                  setAppliedDateTo('');
                }
              }}
              className="w-full px-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-engramiq-bright-grey">User:</label>
                   <div className="relative">
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
                       className="w-40 pl-3 pr-8 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all appearance-none"
                     >
                       {userOptions.map((opt) => (
                         <option key={opt.value} value={opt.value}>
                           {opt.label}
                </option>
              ))}
            </select>
                     <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-engramiq-light-grey pointer-events-none" />
                   </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm text-engramiq-bright-grey">Search:</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-engramiq-bright-grey" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sites, documents, users..."
                className="pl-10 pr-3 py-1.5 h-9 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green transition-all w-64"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-6 mt-4 p-3 bg-accent-red/10 border border-accent-red/30 rounded-lg text-accent-red text-sm">
          {error}
        </div>
      )}

      {/* Sites List */}
      <div className="flex-1 overflow-y-auto p-6">
        {sortedSites.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-engramiq-bright-grey">No As-Built documents found</p>
            <button
              onClick={handleUpload}
              className="mt-4 px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green border border-engramiq-green/30 rounded-lg text-sm text-engramiq-dark-blue transition-all"
            >
              Upload your first As-Built
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedSites.map((site) => {
              const isExpanded = expandedSites.has(site.site_id);

              return (
                <div
                  key={site.site_id}
                  className="bg-surface-800/30 border border-engramiq-green/20 rounded-lg overflow-hidden group"
                >
                  {/* Site Header */}
                  <button
                    onClick={() => toggleSite(site.site_id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-surface-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-engramiq-green flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-engramiq-green flex-shrink-0" />
                      )}
                      <div className="text-left">
                        {editingSiteId === site.site_id ? (
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              value={editedSiteName}
                              onChange={(e) => setEditedSiteName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  saveEditedSiteName(site.site_id);
                                } else if (e.key === 'Escape') {
                                  cancelEditingSite();
                                }
                              }}
                              className="px-3 py-1.5 bg-surface-800 border border-engramiq-green/30 rounded-lg text-sm text-engramiq-light-grey focus:outline-none focus:border-engramiq-green"
                              autoFocus
                              disabled={savingSiteName}
                            />
                            <button
                              onClick={() => saveEditedSiteName(site.site_id)}
                              disabled={savingSiteName || isUpdatingSite}
                              className="p-1.5 bg-engramiq-green hover:bg-engramiq-green/80 rounded text-engramiq-dark-blue transition-all disabled:opacity-50 flex items-center justify-center"
                              title="Save"
                            >
                              {(savingSiteName || isUpdatingSite) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                              <Check className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={cancelEditingSite}
                              disabled={savingSiteName}
                              className="p-1.5 bg-surface-800 hover:bg-surface-700 rounded text-engramiq-light-grey transition-all disabled:opacity-50"
                              title="Cancel"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center gap-2">
                              <h2 className="text-lg font-semibold text-engramiq-light-grey">
                                {site.site_name}
                              </h2>
                              {/* {site.asbuilt_name && site.asbuilt_name !== site.site_name && (
                                <span className="text-xs text-engramiq-bright-grey">
                                  ({toSentenceCase(site.asbuilt_name)})
                                </span>
                              )} */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingSite(site.site_id, site.site_name);
                                }}
                                className="p-1.5 hover:bg-engramiq-green/10 rounded text-engramiq-green/70 hover:text-engramiq-green transition-all"
                                title="Edit site name"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-xs text-engramiq-bright-grey">
                              {site.documents.length} as-built{site.documents.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                  {/* Documents List */}
                  {isExpanded && (
                    <div className="border-t border-engramiq-green/10 bg-engramiq-dark-blue/50">
                      {/* Demo Progress for this site */}
                      {demoUploads.filter(upload => upload.siteId === site.site_id).map((upload) => (
                        <div key={upload.id} className="p-4 border-b border-engramiq-green/10">
                          <ThreeStepProgress
                            currentStep={upload.step}
                            fileName={upload.fileName}
                            onClose={() => handleCloseDemoProgress(upload.id)}
                            onRetry={() => handleRetryDemoUpload(upload.id)}
                            showRetryButton={upload.showRetry !== false}
                            failureType={upload.failureType}
                          />
                          </div>
                      ))}

                      <div className="p-4 space-y-3">
                        {site.documents.map((doc) => {
                          const isSelected = state.selectedSite === site.site_id;

                            return (
                            <div
                              key={doc.document_id}
                              onClick={() => handleSiteSelect(site.site_id)}
                              className={`px-3 py-2 rounded-lg transition-all group cursor-pointer ${
                                isSelected
                                  ? 'bg-engramiq-light-grey/20 hover:bg-white/10'
                                  : 'bg-surface-800/50 hover:bg-white/10'
                              }`}
                            >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-sm font-semibold text-engramiq-light-grey">
                                      {toSentenceCase(site.site_name)}
                                </h3>
                                    {/* {site.asbuilt_name && site.asbuilt_name !== site.site_name && (
                                      <span className="text-xs text-engramiq-bright-grey">
                                        ({toSentenceCase(site.asbuilt_name)})
                                      </span>
                                    )} */}
                                  </div>
                                <div className="flex gap-4 text-xs text-engramiq-bright-grey">
                                  <span>
                                      Uploaded: {formatDateTime(doc.uploaded_at)}
                                  </span>
                                    <span>By: {doc.uploader?.email || 'Not available'}</span>
                                </div>
                              </div>

                              <div className="flex gap-2 ml-4">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(doc, site.site_id);
                                  }}
                                  disabled={downloadingDocId === doc.document_id}
                                  className="p-2 bg-engramiq-dark-blue/80 hover:bg-engramiq-dark-blue border border-engramiq-green/50 hover:border-engramiq-green rounded-lg text-engramiq-green hover:text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Download PDF"
                                >
                                  {downloadingDocId === doc.document_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportExcel(doc);
                                  }}
                                  className="p-2 bg-engramiq-dark-blue/80 hover:bg-engramiq-dark-blue border border-engramiq-green/50 hover:border-engramiq-green rounded-lg text-engramiq-green hover:text-engramiq-green transition-all"
                                  title="Export Excel"
                                >
                                  <FileSpreadsheet className="w-4 h-4" />
                                </button>
                                {/* <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(doc.document_id);
                                  }}
                                  className="p-2 bg-engramiq-dark-blue/80 hover:bg-engramiq-dark-blue border border-engramiq-green/50 hover:border-engramiq-green rounded-lg text-engramiq-green hover:text-engramiq-green transition-all"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button> */}
                              </div>
                            </div>
                          </div>
                            );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-engramiq-dark-blue border border-engramiq-green/30 rounded-lg p-6 max-w-md mx-4 shadow-engramiq-lg">
            <h3 className="text-xl font-bold text-engramiq-light-grey mb-4">
              Delete As-Built Document
            </h3>
            <p className="text-engramiq-bright-grey mb-6">
              Are you sure you want to delete this as-built? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 bg-surface-800 border border-engramiq-green/30 hover:border-engramiq-green rounded-lg text-engramiq-light-grey transition-all disabled:opacity-50"
              >
                No
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-accent-red hover:bg-accent-red/80 border border-accent-red rounded-lg text-white transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Confirmation Modal */}
      <UploadConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelUpload}
        onConfirm={handleConfirmUpload}
      />
    </div>
  );
};

export default AsBuiltListTab;
